import "../../utils/string.extension";
import { useEffect, useState } from "react";
import { endOfDay, startOfMonth, subMonths } from "date-fns";
import { Stack } from "@mui/system";
import { Box, Grid, Typography, useTheme } from "@mui/material";
import { Helmet } from "react-helmet-async";
import { DateRangePickerCustom } from "../../common/components/daterangepicker.custom";
import DashboardMatrix from "./dashboard.matrix";
import DashboardRecentTransactions from "./dashboard.recenttransactions";
import DashboardCardBrandUsage from "./dashboard.cardbrandusage";
import { DashboardDataLoader as DataLoader } from "./dashboard.dataloader";
import { DealerLocationFilter } from "./dashboard.dealerlocationfilter";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { setTransactionSettings } from "../../features/settings/transactionsettings.slice";
import { RootState } from "../../app/store";
import { shouldLoadPageSettings } from "../../features/settings/transactionsettings.slice";
import { useGetUserPreferenceQuery } from "../../services/API/commonApiSlice";
import { userPreferencesStorage } from "../../common/UserPreference/service/userPreferenceStorageService";
import { useGetPaymentsQuery } from "../../services/API/transactionsApiSlice";
import { convertToUTC } from "../../utils/Util";
import { GetCompanyDealerId } from "../../utils/UtilTarget";
import { handleApiError } from "../../services/errorTranslator";

export default function DashboardPerseusPay(props) {
  const dispatch = useAppDispatch();
  const theme = useTheme();

  const loadPageSettings = useAppSelector(
    (state: RootState) => state.transactionSettings.shouldLoadPageSettings
  );
  const authToken = useAppSelector((state: RootState) => state.auth.access_token);

  const today = new Date();
  const [matrixData, setMatrixData] = useState<any>([]);
  const [cardBrandUsages, setCardBrandUsages] = useState<any>();
  const [dealerLocations, setDealerLocations] = useState<any>([]);
  const [dealerLocationsID, setDealerLocationID] = useState<number>(-1);
  const [selectedDateRange, setSelectedDateRange] = useState<[Date, Date]>();
  const defaultDateRange : [Date, Date] = [startOfMonth(today), endOfDay(today)];
  const [company_Dealer_Id, setcompanyDealerId] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);

  const { data: UserPreferencesData, isFetching: isPreferenceFetching } =
    useGetUserPreferenceQuery(undefined);

  useEffect(() => {
    const getCompanyDealerId = async () => {
      var companyDealerId = await GetCompanyDealerId();
      setcompanyDealerId(Number(companyDealerId ?? 0));
    };
    getCompanyDealerId();
  }, [authToken]);

  const getDateCriteria = (): [Date, Date] => {
    return !selectedDateRange
      ? defaultDateRange
      : selectedDateRange;
  }

  useEffect(() => {
    if (UserPreferencesData) {
      userPreferencesStorage.setAll(UserPreferencesData);
    } else {
      userPreferencesStorage.clear();
    }
  }, [UserPreferencesData, isPreferenceFetching]);

  useEffect(() => {
    DataLoader.GetDealerLocations(props.DealerId).then((response) => {
      setDealerLocations(response);
    });

    DataLoader.GetPageSettings().then((response) => {
      dispatch(setTransactionSettings(response));
      dispatch(shouldLoadPageSettings(false));
    });
  }, []);

  useEffect(() => {

    DataLoader.GetTransactionMatrix(getDateCriteria(), dealerLocationsID, 10).then(
      (response) => {
        setMatrixData(response?.length > 0 ? response[0] : {});
      },
    );

    // DataLoader.GetCardBrandUsageData(getDateCriteria(), dealerLocationsID).then(
    //   (response) => {
    //     setCardBrandUsages(response);
    //   }
    // );

    // DataLoader.GetPayments(dateCriteria, dealerLocationsID, 10).then(
    //   (response) => {
    //     setRecentsTransactions(response);
    //   }
    // );
  }, [dealerLocationsID, selectedDateRange]);

  
  /**
   * Fetch transactions via RTK Query.
   * Triggers refetch when pagination, search, filters, or sorting changes.
   */
  const {
    data: transactionsResponse,
    isFetching,
    error,
    refetch,
    isUninitialized,
  } = useGetPaymentsQuery(
    {
      dealerId: dealerLocationsID === -1 ? 0 : dealerLocationsID,
      companyDealerId: dealerLocationsID === -1 ? company_Dealer_Id : 0,
      Page: 1,
      PageSize: 10,
      fromDate: convertToUTC(getDateCriteria()[0]).trim(),
      toDate: convertToUTC(getDateCriteria()[1], "end").trim(),
      searchCriteria: "",
      sortField: "dateCreated",
      sortDirection: "desc",
      requestSource: 2 //Dashboard Page
    },
    { skip: company_Dealer_Id <= 0, refetchOnMountOrArgChange: true },
  );

  useEffect(() => {
    if (!isFetching && error) {
      handleApiError(error);
    }
  }, [error, isFetching]);

  const handleDateRangePickerChange = (value: [Date, Date]) => {
    setSelectedDateRange(value);
  };

  const handleDealerLocationChange = (value) => {
    setDealerLocationID(value);
  };

  return (
    <>
      <Helmet>
        <title> Dashboard | {theme.browserTitle} </title>
      </Helmet>
      <Box>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
          style={{ border: "0px solid red", margin: "0px" }}
        >
          <Typography variant="h6" gutterBottom style={{ fontWeight: "bold" }}>
            Dashboard
          </Typography>
          <Stack direction="row" alignItems="center">
            <DealerLocationFilter
              dealerLocationsID={dealerLocationsID}
              dealerLocations={dealerLocations}
              onDealerLocationChange={handleDealerLocationChange}
            ></DealerLocationFilter>
            <DateRangePickerCustom
              dateRange={defaultDateRange}
              onDateRangePickerChange={handleDateRangePickerChange}
              isDepositReport={false}
            ></DateRangePickerCustom>
          </Stack>
        </Stack>
        <DashboardMatrix paymentData={matrixData}></DashboardMatrix>
      </Box>
      <br />
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <DashboardRecentTransactions
            transactions={transactionsResponse}
            isTransactionsFetching={isFetching || isUninitialized}
          ></DashboardRecentTransactions>
        </Grid>

        {/* Hide Card Brand Usage Section for time being. */}
        {/* Don't Uncomment/Delete */}
        {/* <Grid item xs={4}>
          <DashboardCardBrandUsage
            cardBrandUsages={cardBrandUsages}
          ></DashboardCardBrandUsage>
        </Grid> */}
      </Grid>
    </>
  );
}
